import api from './api';

// ── Types ──────────────────────────────────────────────────────────────────

export interface ControlType {
  id: number;
  code: string;
  displayName: string;
  description?: string;
  periodDays?: number | null;
  maxHours?: number | null;
  extensionsAllowed?: boolean;
  exhaustionMode?: string;
  toolSeatJson?: string;
}

export interface LicenseGroupType {
  id: number;
  code: string;
  displayName: string;
  description?: string;
  toolsJson?: string;
}

export interface WorkspacePackage {
  id: number;
  code: string;
  displayName: string;
  description?: string;
  licenseGroupTypeId?: number | null;
  controlTypeId?: number | null;
  toolsJson?: string;
  licenseCountsJson?: string;
  /** IWM catalog profile id — picked from WorkspaceService.getProfiles(), not free text. */
  iwmProfileId?: string | null;
}

export interface IwmProfile {
  id: string;
  displayName?: string;
  [key: string]: unknown;
}

export interface GroupType {
  id: number;
  code: string;
  kind: string;
  displayName: string;
  description?: string;
}

export type NfsRole = 'tools' | 'labs' | 'pdk' | 'home';

export interface NfsLocation {
  id: number;
  code: string;
  role: NfsRole;
  nfsServer: string;
  nfsPath: string;
  mountPath: string;
  readOnly: boolean;
}

export interface GroupMember {
  id?: number;
  userId: string;
  email?: string;
  isPrimary?: boolean;
}

export interface GroupInstance {
  id: number;
  name?: string;
  code?: string;
  startDate?: string | null;
  endDate?: string | null;
  packageId?: number | null;
  controlTypeId?: number | null;
  toolsNfsLocationId?: number | null;
  labsNfsLocationId?: number | null;
  pdkNfsLocationId?: number | null;
  homeNfsLocationId?: number | null;
  affinityServerIds?: string;
  affinityServerIdsJson?: string;
  scheduledHours?: number | null;
  scheduledHoursJson?: string;
  maxWeekHours?: number | null;
  profileId?: string;
  groupTypeId?: number;
  extensionPossible?: boolean;
  status?: string;
  members?: GroupMember[];
}

export interface WorkspaceSession {
  id: string;
  user: string;
  profileId: string;
  status?: string;
  createdAt?: string;
  novncUrl?: string;
  [key: string]: unknown;
}

// ── Definitions ────────────────────────────────────────────────────────────

const defs = '/api/workspace/definitions';

export const listControlTypes = () =>
  api.get<ControlType[]>(`${defs}/control-types`).then((r) => r.data);

export const createControlType = (data: Partial<ControlType>) =>
  api.post<ControlType>(`${defs}/control-types`, data).then((r) => r.data);

export const updateControlType = (id: number, data: Partial<ControlType>) =>
  api.put<ControlType>(`${defs}/control-types/${id}`, data).then((r) => r.data);

export const deleteControlType = (id: number) =>
  api.delete(`${defs}/control-types/${id}`);

export const listLicenseGroupTypes = () =>
  api.get<LicenseGroupType[]>(`${defs}/license-group-types`).then((r) => r.data);

export const createLicenseGroupType = (data: Partial<LicenseGroupType>) =>
  api.post<LicenseGroupType>(`${defs}/license-group-types`, data).then((r) => r.data);

export const updateLicenseGroupType = (id: number, data: Partial<LicenseGroupType>) =>
  api.put<LicenseGroupType>(`${defs}/license-group-types/${id}`, data).then((r) => r.data);

export const deleteLicenseGroupType = (id: number) =>
  api.delete(`${defs}/license-group-types/${id}`);

export const listPackages = () =>
  api.get<WorkspacePackage[]>(`${defs}/packages`).then((r) => r.data);

export const createPackage = (data: Partial<WorkspacePackage>) =>
  api.post<WorkspacePackage>(`${defs}/packages`, data).then((r) => r.data);

export const updatePackage = (id: number, data: Partial<WorkspacePackage>) =>
  api.put<WorkspacePackage>(`${defs}/packages/${id}`, data).then((r) => r.data);

export const deletePackage = (id: number) =>
  api.delete(`${defs}/packages/${id}`);

/** Live IWM profile catalog (for the Package "IWM Profile" picker) — not free text. */
export const listIwmProfiles = () =>
  api.get<IwmProfile[]>('/api/workspace/iwm/profiles').then((r) => r.data);

export const listGroupTypes = () =>
  api.get<GroupType[]>(`${defs}/group-types`).then((r) => r.data);

export const createGroupType = (data: Partial<GroupType>) =>
  api.post<GroupType>(`${defs}/group-types`, data).then((r) => r.data);

export const updateGroupType = (id: number, data: Partial<GroupType>) =>
  api.put<GroupType>(`${defs}/group-types/${id}`, data).then((r) => r.data);

export const deleteGroupType = (id: number) =>
  api.delete(`${defs}/group-types/${id}`);

export const seedDefinitions = () =>
  api.post(`${defs}/seed`).then((r) => r.data);

// ── NFS locations ──────────────────────────────────────────────────────────

export const listNfsLocations = () =>
  api.get<NfsLocation[]>('/api/workspace/nfs-locations').then((r) => r.data);

export const createNfsLocation = (data: Partial<NfsLocation>) =>
  api.post<NfsLocation>('/api/workspace/nfs-locations', data).then((r) => r.data);

export const updateNfsLocation = (id: number, data: Partial<NfsLocation>) =>
  api.put<NfsLocation>(`/api/workspace/nfs-locations/${id}`, data).then((r) => r.data);

export const deleteNfsLocation = (id: number) =>
  api.delete(`/api/workspace/nfs-locations/${id}`);

// ── Group instances ────────────────────────────────────────────────────────

export const listGroupInstances = () =>
  api.get<GroupInstance[]>('/api/workspace/groups').then((r) => r.data);

export const getGroupInstance = (id: number) =>
  api.get<GroupInstance>(`/api/workspace/groups/${id}`).then((r) => r.data);

export const createGroupInstance = (data: Partial<GroupInstance>) =>
  api.post<GroupInstance>('/api/workspace/groups', data).then((r) => r.data);

export const updateGroupInstance = (id: number, data: Partial<GroupInstance>) =>
  api.put<GroupInstance>(`/api/workspace/groups/${id}`, data).then((r) => r.data);

export const deleteGroupInstance = (id: number) =>
  api.delete(`/api/workspace/groups/${id}`);

export const addGroupMember = (groupId: number, data: Partial<GroupMember>) =>
  api.post<GroupMember>(`/api/workspace/groups/${groupId}/members`, data).then((r) => r.data);

export const removeGroupMember = (groupId: number, memberId: number) =>
  api.delete(`/api/workspace/groups/${groupId}/members/${memberId}`);

export const resolveGroupPolicy = (id: number) =>
  api.post(`/api/workspace/groups/${id}/resolve-policy`).then((r) => r.data);

// ── Sessions (IWM) ─────────────────────────────────────────────────────────

export const listWorkspaceSessions = () =>
  api.get<WorkspaceSession[]>('/api/workspace/sessions').then((r) => r.data);

export const createWorkspaceSession = (data: { user: string; profileId: string }) =>
  api.post<WorkspaceSession>('/api/workspace/sessions', data).then((r) => r.data);

export const deleteWorkspaceSession = (id: string) =>
  api.delete(`/api/workspace/sessions/${id}`);
